// NOKE 手冊 — the third tab of the counter POS page.
//
// What staff need at the counter is not a document, it is the answer to one
// question with a customer standing there. So this is NOT a PDF reader:
//
//   情境卡  a curated question ("刷卡的單要退") -> the exact manual pages that
//           answer it, in order, with the one line they must not get wrong on top.
//   搜尋    one box over the 情境卡, the 商場 policy sections, our own 門市工作手冊,
//           and every rendered manual page's caption.
//   每日必做 the three closing steps that carry an actual fine (日結 / 入金 / 空櫃).
//
// Nothing here is generated: every card points at pages of the mall's own manual
// (rendered server-side, served behind counterAuth) and every policy answer is
// quoted from it. A wrong answer here is spoken to a customer's face, so the UI
// shows the source page rather than paraphrasing it.
//
// Pages are fetched as blobs, not <img src>: the API authenticates with a Bearer
// header that an <img> tag cannot send.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SearchIcon from '@mui/icons-material/Search';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import api from '../../services/api';

// ---- types (mirror scripts/noke/build_manual_index.py output) --------------

type DocKey = 'pos' | 'posfull';
type PageRef = [DocKey, number];

interface ManualCard {
  id: string;
  group: string;
  title: string;
  keywords?: string[];
  steps?: string[];
  warn?: string;
  answer?: string;
  pages: PageRef[];
  policy_refs?: string[];
}

interface PolicySection {
  id: string;
  source: string;
  title: string;
  text: string;
}

interface ChecklistItem {
  id: string;
  when: string;
  text: string;
  penalty?: string;
  card?: string;   // -> a 情境卡 with the POS steps
  policy?: string; // -> the written rule behind it
}

interface ManualIndex {
  built_at: string;
  docs: Record<DocKey, { title: string; dated: string; pages: number[]; total_pages: number }>;
  daily_checklist: ChecklistItem[];
  cards: ManualCard[];
  policy: PolicySection[];
  pages: Array<{ doc: DocKey; page: number; text: string }>;
}

// ---- checklist persistence -------------------------------------------------

const CHECKLIST_KEY = 'nokeManualChecklist';

// Counter day, not UTC day: 'sv-SE' formats as YYYY-MM-DD.
const todayTaipei = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Taipei' });

const readChecklist = (): { date: string; done: string[] } => {
  try {
    const raw = localStorage.getItem(CHECKLIST_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.date === todayTaipei() && Array.isArray(parsed.done)) return parsed;
    }
  } catch {
    // Private mode / LINE-IG webview can throw on access — the checklist is a
    // convenience, never a source of truth.
  }
  return { date: todayTaipei(), done: [] };
};

const writeChecklist = (state: { date: string; done: string[] }) => {
  try {
    localStorage.setItem(CHECKLIST_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
};

// ---- one rendered manual page ---------------------------------------------

const ManualPageImage: React.FC<{
  doc: DocKey;
  page: number;
  onZoom?: (url: string) => void;
}> = ({ doc, page, onZoom }) => {
  const [url, setUrl] = useState<string | null>(null);
  // 'missing' = the page really isn't in the bundle; 'offline' = we couldn't
  // reach the server. The index caches for 5 minutes and the images don't, so on
  // mall wifi a fully-rendered card with dead images is the NORMAL failure — the
  // two cases must not read the same.
  const [failed, setFailed] = useState<null | 'missing' | 'offline'>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let objectUrl: string | null = null;
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/api/noke/manual/page/${doc}/${page}`, {
          responseType: 'blob',
        });
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setFailed(null);
        setUrl(objectUrl);
      } catch (e: any) {
        if (alive) setFailed(e?.response?.status === 404 ? 'missing' : 'offline');
      }
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc, page, attempt]);

  if (failed) {
    return (
      <Alert
        severity="warning"
        sx={{ mb: 1 }}
        action={
          failed === 'offline' ? (
            <Button color="inherit" size="small" onClick={() => setAttempt((a) => a + 1)}>
              重試
            </Button>
          ) : undefined
        }
      >
        {failed === 'missing'
          ? `這一頁沒有收錄（${doc === 'pos' ? '簡易版' : '完整版'} 第 ${page} 頁）`
          : `連不上系統，圖片載不出來（${doc === 'pos' ? '簡易版' : '完整版'} 第 ${page} 頁）。確認網路後按重試。`}
      </Alert>
    );
  }
  if (!url) return <Skeleton variant="rectangular" height={280} sx={{ mb: 1, borderRadius: 1 }} />;

  return (
    <Box sx={{ position: 'relative', mb: 1.5 }}>
      <Box
        component="img"
        src={url}
        alt={`手冊第 ${page} 頁`}
        onClick={() => onZoom?.(url)}
        sx={{
          width: '100%',
          display: 'block',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          cursor: onZoom ? 'zoom-in' : 'default',
        }}
      />
      <Chip
        size="small"
        icon={<ZoomInIcon />}
        label={`${doc === 'pos' ? '簡易版' : '完整版'} p.${page}`}
        sx={{ position: 'absolute', right: 8, bottom: 8, bgcolor: 'rgba(255,255,255,0.9)' }}
        onClick={() => onZoom?.(url)}
      />
    </Box>
  );
};

// ---- main ------------------------------------------------------------------

const NokeManualTab: React.FC = () => {
  const [index, setIndex] = useState<ManualIndex | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [openCard, setOpenCard] = useState<ManualCard | null>(null);
  const [openPolicy, setOpenPolicy] = useState<PolicySection | null>(null);
  const [openPage, setOpenPage] = useState<{ doc: DocKey; page: number } | null>(null);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [browsing, setBrowsing] = useState<DocKey | null>(null);
  const [checklist, setChecklist] = useState(readChecklist);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/api/noke/manual');
        if (alive) setIndex(res.data);
      } catch (e: any) {
        if (alive) setLoadError(e?.response?.data?.message || e?.message || '載入失敗');
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const toggleCheck = useCallback((id: string) => {
    setChecklist((prev) => {
      const base = prev.date === todayTaipei() ? prev : { date: todayTaipei(), done: [] };
      const done = base.done.includes(id)
        ? base.done.filter((d) => d !== id)
        : [...base.done, id];
      const next = { date: base.date, done };
      writeChecklist(next);
      return next;
    });
  }, []);

  const policyById = useMemo(() => {
    const map = new Map<string, PolicySection>();
    index?.policy.forEach((p) => map.set(p.id, p));
    return map;
  }, [index]);

  // Substring matching on every token — Chinese needs no tokenizer, and staff
  // type fragments ("退貨 刷卡") rather than the manual's own wording.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!index || !q) return null;
    const tokens = q.split(/\s+/).filter(Boolean);
    const hit = (hay: string) => tokens.every((t) => hay.toLowerCase().includes(t));

    const cards = index.cards.filter((c) =>
      hit(
        [c.title, c.group, (c.keywords || []).join(' '), (c.steps || []).join(' '), c.warn || '', c.answer || ''].join(' ')
      )
    );
    const policy = index.policy.filter((p) => hit(`${p.title} ${p.text}`));
    const pages = index.pages.filter((p) => hit(p.text)).slice(0, 20);
    return { cards, policy, pages };
  }, [index, query]);

  const groups = useMemo(() => {
    if (!index) return [];
    const order: string[] = [];
    const byGroup = new Map<string, ManualCard[]>();
    index.cards.forEach((c) => {
      if (!byGroup.has(c.group)) {
        byGroup.set(c.group, []);
        order.push(c.group);
      }
      byGroup.get(c.group)!.push(c);
    });
    return order.map((g) => ({ group: g, cards: byGroup.get(g)! }));
  }, [index]);

  if (loadError) {
    return <Alert severity="error">手冊載入失敗：{loadError}</Alert>;
  }
  if (!index) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ---- detail: one 情境卡 --------------------------------------------------

  if (openCard) {
    const card = openCard;
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setOpenCard(null)} sx={{ mb: 1 }}>
          返回
        </Button>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {card.title}
        </Typography>

        {card.warn && (
          <Alert severity="warning" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {card.warn}
          </Alert>
        )}

        {card.answer && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
              {card.answer}
            </Typography>
          </Paper>
        )}

        {!!card.steps?.length && (
          <Box component="ol" sx={{ pl: 3, m: 0, mb: 2 }}>
            {card.steps.map((s, i) => (
              <Typography component="li" key={i} variant="body1" sx={{ mb: 1, lineHeight: 1.7 }}>
                {s}
              </Typography>
            ))}
          </Box>
        )}

        {card.pages.map(([doc, page]) => (
          <ManualPageImage key={`${doc}-${page}`} doc={doc} page={page} onZoom={setZoomUrl} />
        ))}

        {(card.policy_refs || []).map((ref) => {
          const section = policyById.get(ref);
          if (!section) return null;
          return (
            <Accordion key={ref} disableGutters sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" color="text.secondary">
                  {section.source}｜{section.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                  {section.text}
                </Typography>
              </AccordionDetails>
            </Accordion>
          );
        })}

        <ZoomDialog url={zoomUrl} onClose={() => setZoomUrl(null)} />
      </Box>
    );
  }

  // ---- detail: one policy section -----------------------------------------

  if (openPolicy) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setOpenPolicy(null)} sx={{ mb: 1 }}>
          返回
        </Button>
        <Typography variant="h6">{openPolicy.title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {openPolicy.source}
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>
            {openPolicy.text}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ---- detail: one manual page --------------------------------------------

  if (openPage) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setOpenPage(null)} sx={{ mb: 1 }}>
          返回
        </Button>
        <ManualPageImage doc={openPage.doc} page={openPage.page} onZoom={setZoomUrl} />
        <ZoomDialog url={zoomUrl} onClose={() => setZoomUrl(null)} />
      </Box>
    );
  }

  // ---- browse a whole manual ----------------------------------------------

  if (browsing) {
    const doc = index.docs[browsing];
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setBrowsing(null)} sx={{ mb: 1 }}>
          返回
        </Button>
        <Typography variant="h6">{doc.title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {doc.dated}｜收錄 {doc.pages.length} 頁
        </Typography>
        <List dense sx={{ mt: 1 }}>
          {index.pages
            .filter((p) => p.doc === browsing)
            .map((p) => (
              <ListItemButton
                key={p.page}
                divider
                onClick={() => setOpenPage({ doc: p.doc, page: p.page })}
              >
                <ListItemText
                  primary={`p.${p.page}　${p.text.slice(0, 34) || '（圖）'}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItemButton>
            ))}
        </List>
      </Box>
    );
  }

  // ---- list / search ------------------------------------------------------

  const checklistDone = checklist.date === todayTaipei() ? checklist.done : [];

  return (
    <Box>
      <TextField
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="搜尋：退貨、統編、日結、停車、點數…"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setQuery('')} aria-label="清除搜尋">
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ mb: 2 }}
      />

      {results ? (
        <>
          {results.cards.length === 0 && results.policy.length === 0 && results.pages.length === 0 && (
            <Alert severity="info">
              找不到「{query}」。試試更短的詞（例如「退貨」「日結」「停車」），或按下面的「翻整本手冊」。
            </Alert>
          )}

          {results.cards.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary">
                情境
              </Typography>
              <List dense sx={{ mb: 2 }}>
                {results.cards.map((c) => (
                  <ListItemButton key={c.id} divider onClick={() => setOpenCard(c)}>
                    <ListItemText primary={c.title} secondary={c.group} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          {results.policy.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary">
                規定・說明
              </Typography>
              <List dense sx={{ mb: 2 }}>
                {results.policy.map((p) => (
                  <ListItemButton key={p.id} divider onClick={() => setOpenPolicy(p)}>
                    <ListItemText primary={p.title} secondary={p.source} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}

          {results.pages.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary">
                手冊頁面
              </Typography>
              <List dense>
                {results.pages.map((p) => (
                  <ListItemButton
                    key={`${p.doc}-${p.page}`}
                    divider
                    onClick={() => setOpenPage({ doc: p.doc, page: p.page })}
                  >
                    <ListItemText
                      primary={p.text.slice(0, 40) || `第 ${p.page} 頁`}
                      secondary={`${index.docs[p.doc].title} p.${p.page}`}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </>
      ) : (
        <>
          <Accordion disableGutters defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                每日必做
                <Chip
                  size="small"
                  color={checklistDone.length >= index.daily_checklist.length ? 'success' : 'default'}
                  label={`${checklistDone.length}/${index.daily_checklist.length}`}
                  sx={{ ml: 1 }}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {index.daily_checklist.map((item) => (
                <Box key={item.id} sx={{ mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checklistDone.includes(item.id)}
                        onChange={() => toggleCheck(item.id)}
                      />
                    }
                    label={
                      <Box>
                        {/* component="div": the 時段 Chip renders a <div>, which
                            is invalid inside Typography's default <p>. */}
                        <Typography variant="body2" component="div">
                          <Chip size="small" label={item.when} sx={{ mr: 1, height: 20 }} />
                          {item.text}
                        </Typography>
                        {item.penalty && (
                          <Typography variant="caption" color="error">
                            {item.penalty}
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                  {item.card && (
                    <Button
                      size="small"
                      sx={{ ml: 4 }}
                      onClick={() => {
                        const target = index.cards.find((c) => c.id === item.card);
                        if (target) setOpenCard(target);
                      }}
                    >
                      看操作步驟
                    </Button>
                  )}
                  {item.policy && (
                    <Button
                      size="small"
                      sx={{ ml: item.card ? 0 : 4 }}
                      onClick={() => {
                        const target = policyById.get(item.policy!);
                        if (target) setOpenPolicy(target);
                      }}
                    >
                      看規定
                    </Button>
                  )}
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary">
                打勾只存在這台裝置，換日自動清空。
              </Typography>
            </AccordionDetails>
          </Accordion>

          {groups.map(({ group, cards }) => (
            <Box key={group} sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary">
                {group}
              </Typography>
              <List dense disablePadding>
                {cards.map((c) => (
                  <ListItemButton key={c.id} divider onClick={() => setOpenCard(c)}>
                    <ListItemText
                      primary={c.title}
                      primaryTypographyProps={{ variant: 'body1' }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary">
            翻整本手冊
          </Typography>
          <List dense>
            {(Object.keys(index.docs) as DocKey[]).map((key) => (
              <ListItemButton key={key} divider onClick={() => setBrowsing(key)}>
                <MenuBookIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
                <ListItemText
                  primary={index.docs[key].title}
                  secondary={`${index.docs[key].dated}｜收錄 ${index.docs[key].pages.length} 頁`}
                />
              </ListItemButton>
            ))}
          </List>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            內容依商場提供之手冊；規定以商場最新公告為準。
          </Typography>
        </>
      )}

      <ZoomDialog url={zoomUrl} onClose={() => setZoomUrl(null)} />
    </Box>
  );
};

// Full-screen page view — the counter iPad/phone is small and the screenshots
// carry the answer, so a tap has to be able to fill the screen.
const ZoomDialog: React.FC<{ url: string | null; onClose: () => void }> = ({ url, onClose }) => (
  <Dialog open={!!url} onClose={onClose} fullScreen>
    <DialogContent sx={{ p: 0, bgcolor: 'grey.900' }}>
      <IconButton
        onClick={onClose}
        sx={{ position: 'fixed', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.9)', zIndex: 1 }}
        aria-label="關閉"
      >
        <CloseIcon />
      </IconButton>
      {url && <Box component="img" src={url} alt="" sx={{ width: '100%', display: 'block' }} />}
    </DialogContent>
  </Dialog>
);

export default NokeManualTab;
